/**
 * Pure decision logic for the post-Keycloak registration flow: a Keycloak-authenticated
 * user with no member yet (GET /v2/members/me -> 404) has to be routed to the company
 * he will register into. Mirrors the mobile company-resolution cascade, with every
 * branch driven by S3 brand-config data instead of brand names.
 *
 * Everything here is pure and unit-tested (ssoRegistrationFlow.test.js); components
 * and App consume the decisions through lookup tables instead of re-implementing the
 * branches.
 */

// Where a Keycloak user with no member is sent once the automatic e-mail resolution
// (GET /v2/companies/sso-registration) has come back empty.
export const FALLBACK_DECISION = {
  FLAGS: 'FLAGS',
  SINGLE_COMPANY: 'SINGLE_COMPANY',
  COMPANY_PICKER: 'COMPANY_PICKER',
  INVITE_ONLY: 'INVITE_ONLY',
  UNAVAILABLE: 'UNAVAILABLE'
};

// Terminal states of the shared chooser screen body (CompanyChooser).
export const CHOOSER_STATE = {
  LOADING: 'LOADING',
  LIST: 'LIST',
  INVITE_ONLY: 'INVITE_ONLY',
  UNAVAILABLE: 'UNAVAILABLE'
};

// Every external (Keycloak) registration submits here. The endpoint is a JWT contract:
// the server takes the e-mail from the token, so the body carries no `login`. Kept as
// a single constant so switching the resolve path back to /v2/members/sso (should the
// backend require it) stays a one-line change.
export const EXTERNAL_REGISTRATION_URL = '/v2/members/keycloak';

// The two S3 keycloak-block booleans behind the manual-registration fork are strict
// opt-ins: only a literal `true` enables the behaviour - absent / false / null /
// anything else reads as false, which keeps partially migrated S3 configs
// deterministic.
export function getRegistrationFlags(keycloak) {
  const block = keycloak || {};

  return {
    useCompanyFlags: block.useCompanyFlags === true,
    allowRegisterWithInvitationCode: block.allowRegisterWithInvitationCode === true
  };
}

// GET /v2/brands/{id}/super-companies/public answers with a paginated `{results}`
// envelope (the shape the mobile client consumes); a bare array is accepted for
// compatibility. Anything else means the response was not understood: `null`, i.e.
// "not loaded" - never an empty brand.
export function normalizePublicCompaniesResponse(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  return null;
}

// Companies the user already holds a profile in are not offered again - the pickers
// also serve the add-profile entry (mirror of the mobile PublicCompanies filter).
// With no profiles loaded (first registration: /me was 404) nothing is filtered.
export function filterUnlinkedCompanyItems(items, profileCompanyIds, getCompanyId) {
  if (!items || !profileCompanyIds?.length) return items;

  return items.filter((item) => !profileCompanyIds.includes(getCompanyId(item)));
}

// The mobile cascade over the public-companies response, one rule per row; evaluated
// in order, the first matching row wins.
const COMPANY_COUNT_DECISIONS = [
  { matches: ({ count }) => count === 1, decision: FALLBACK_DECISION.SINGLE_COMPANY },
  { matches: ({ count }) => count > 1, decision: FALLBACK_DECISION.COMPANY_PICKER },
  { matches: ({ allowInvite }) => allowInvite, decision: FALLBACK_DECISION.INVITE_ONLY },
  { matches: () => true, decision: FALLBACK_DECISION.UNAVAILABLE }
];

export function getPublicCompanyFallbackDecision({ availableCompanies, allowInvite }) {
  const count = availableCompanies.length;

  return COMPANY_COUNT_DECISIONS.find((rule) => rule.matches({ count, allowInvite }))
    .decision;
}

// Terminal state of the shared chooser body: `items` null means still loading, an
// empty list degrades to the invitation code when the brand allows it and to an
// honest "registration unavailable" otherwise.
export function getChooserState(items, allowInvite) {
  if (!items) return CHOOSER_STATE.LOADING;
  if (items.length > 0) return CHOOSER_STATE.LIST;
  return allowInvite ? CHOOSER_STATE.INVITE_ONLY : CHOOSER_STATE.UNAVAILABLE;
}

// What the company picker hands the chooser while the linked-profile facts of a
// logged-in visitor are still being settled: nothing, i.e. the chooser's loading
// state - otherwise a hard refresh on the add-profile entry would render the
// UNFILTERED list (filterUnlinkedCompanyItems no-ops on missing ids) with
// already-linked companies clickable. Once the attempt has finished the list
// goes through even after a FAILED profiles fetch: that is the contract - an
// unfiltered list over an endless spinner (the fetch error has already been
// flashed, and the server still rejects a duplicate link on submit).
export function chooserItemsForProfileFacts(attemptFinished, items) {
  return attemptFinished ? items : null;
}
